import React from 'react';
import { NavLink } from 'react-router-dom';
import type { AdminHealthStatus } from '../types';
import AdminIcon, { type AdminIconName } from './AdminIcon';

export interface AdminShellCounts {
  companies: number;
  errorReports: number;
  licenses: number;
  newRequests: number;
  requests: number;
  users: number;
}

interface AdminShellProps {
  children: React.ReactNode;
  counts: AdminShellCounts;
  health: AdminHealthStatus;
  identity: string;
}

interface AdminRouteErrorStackProps {
  active: boolean;
  children: React.ReactNode;
}

interface AdminNavItem {
  count?: keyof AdminShellCounts;
  icon: AdminIconName;
  label: string;
  path: string;
}

const navItems: AdminNavItem[] = [
  { icon: 'dashboard', label: 'Dashboard', path: '/super-admin/dashboard' },
  { count: 'licenses', icon: 'licenses', label: 'Licenses', path: '/super-admin/licenses' },
  { count: 'users', icon: 'users', label: 'Users', path: '/super-admin/users' },
  { count: 'companies', icon: 'companies', label: 'Companies', path: '/super-admin/companies' },
  { icon: 'demoLeads', label: 'Demo leads', path: '/super-admin/demo-leads' },
  { icon: 'documents', label: 'Documents', path: '/super-admin/documents' },
  { count: 'requests', icon: 'requests', label: 'Requests', path: '/super-admin/requests' },
];

const navClassName = (
  item: AdminNavItem,
  isActive: boolean,
  counts: AdminShellCounts
): string => {
  const classes = ['admin-tab'];
  if (isActive) {
    classes.push('admin-tab--active');
  }
  if (item.count === 'requests' && counts.newRequests > 0) {
    classes.push('admin-tab--hot');
  }
  return classes.join(' ');
};

export const AdminRouteErrorStack: React.FC<AdminRouteErrorStackProps> = ({
  active,
  children,
}) => active ? <div className="admin-route-error-stack">{children}</div> : <>{children}</>;

const AdminShell: React.FC<AdminShellProps> = ({
  children,
  counts,
  health,
  identity,
}) => (
  <main
    aria-label="PipeEX admin workspace"
    className="pipeex-admin pipeex-admin-root admin-shell"
  >
    <header className="admin-header">
      <div className="admin-header__brand">
        <h1>PipeEX admin</h1>
        <p>Licensing &middot; accounts &middot; support</p>
      </div>
      <div className="admin-header__actions">
        <NavLink
          className="admin-header__error-link"
          to="/super-admin/requests?category=ErrorReport"
        >
          <AdminIcon name="warning" />
          <span>Error reports</span>
          <span className="admin-count admin-count--hot">{counts.errorReports}</span>
        </NavLink>
        <span className="admin-identity">
          <span
            aria-label={`License server ${health}`}
            className={`admin-identity__dot admin-identity__dot--${health}`}
            role="img"
          />
          {identity}
        </span>
      </div>
    </header>

    <nav aria-label="Admin sections" className="admin-tabs">
      {navItems.map((item) => (
        <NavLink
          className={({ isActive }) => navClassName(item, isActive, counts)}
          key={item.path}
          to={item.path}
        >
          <AdminIcon name={item.icon} />
          <span>{item.label}</span>
          {item.count && (
            <span className="admin-count">{counts[item.count]}</span>
          )}
        </NavLink>
      ))}
    </nav>

    <div className="admin-shell__content">{children}</div>
  </main>
);

export default AdminShell;
